import React, { useState, useMemo } from 'react';
import {
    Stack,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { Order, paymentMethodsTranslations } from '@api/Orders/Order';
import { getHours, format, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { currencyFormatter } from '@utils/Numbers';
import { BarChart as BarIcon, SsidChart as LineIcon } from '@mui/icons-material';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';

interface DashboardAnalyticsProps {
    orders: Order[];
}

type MetricType = 'sales' | 'count';
type ViewType = 'hour' | 'dayOfWeek' | 'paymentMethod';
type ChartType = 'bar' | 'line';

// Paleta de colores más viva
const CHART_COLORS = {
    sales: '#007AFF', // Azul vivo
    count: '#FF9500' // Naranja vibrante
};
const PIE_COLORS = ['#007AFF', '#FF3B30', '#34C759', '#FFCC00', '#AF52DE'];

const DashboardAnalytics: React.FunctionComponent<DashboardAnalyticsProps> = ({ orders }) => {
    const [metric, setMetric] = useState<MetricType>('sales');
    const [view, setView] = useState<ViewType>('hour');
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string>('all');

    const chartData = useMemo(() => {
        if (!orders || orders.length === 0) return [];

        if (view === 'hour') {
            const hoursMap = new Map<number, { name: string; sales: number; count: number }>();
            for (let i = 0; i < 24; i++) {
                hoursMap.set(i, { name: `${i}:00`, sales: 0, count: 0 });
            }

            orders.forEach((order) => {
                const orderDate = order.date.toDate();

                // Si hay un día específico seleccionado, saltamos los que no coincidan
                // getDay() retorna 0 para Domingo, 1 para Lunes, etc.
                if (selectedDayOfWeek !== 'all' && getDay(orderDate).toString() !== selectedDayOfWeek) {
                    return;
                }

                const hour = getHours(orderDate);
                const current = hoursMap.get(hour)!;
                current.sales += order.total;
                current.count += 1;
            });

            // Opcional: Filtramos las horas en ceros si quieres que la gráfica se vea más compacta
            return Array.from(hoursMap.values()).filter((h) => h.sales > 0 || h.count > 0);
        }

        if (view === 'paymentMethod') {
            const methodsMap = new Map<string, { name: string; sales: number; count: number }>();
            orders.forEach((order) => {
                let method = 'Otro';
                if (order.stripe) method = 'Stripe';
                else if (getOrderPaidBy(order, PaymentMethod.Cash) > 0) method = 'CashMXN';
                else if (getOrderPaidBy(order, PaymentMethod.CreditCard) > 0) method = 'CreditCard';
                else if (getOrderPaidBy(order, PaymentMethod.BankTransfer) > 0) method = 'BankTransfer';

                const existing = methodsMap.get(method) || {
                    name: paymentMethodsTranslations[method] || method,
                    sales: 0,
                    count: 0
                };
                existing.sales += order.total;
                existing.count += 1;
                methodsMap.set(method, existing);
            });
            return Array.from(methodsMap.values());
        }

        // Default: dayOfWeek
        const daysMap = new Map<string, { name: string; sales: number; count: number }>();

        // Inicializamos los días para mantener el orden de la semana
        const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        daysOfWeek.forEach((day) => daysMap.set(day, { name: day, sales: 0, count: 0 }));

        orders.forEach((order) => {
            const dayName = format(order.date.toDate(), 'EEEE', { locale: es });
            const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
            const existing = daysMap.get(capitalizedDay);
            if (existing) {
                existing.sales += order.total;
                existing.count += 1;
            }
        });
        return Array.from(daysMap.values()).filter((d) => d.sales > 0 || d.count > 0);
    }, [orders, view, selectedDayOfWeek]);

    const activeColor = CHART_COLORS[metric];

    return (
        <Stack spacing={4}>
            {/* Controles Principales */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                <Typography variant="h6" fontWeight={600} sx={{ minWidth: 150 }}>
                    Análisis de Ventas
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={view}
                        exclusive
                        onChange={(e, val) => val && setView(val)}
                        size="small"
                    >
                        <ToggleButton value="hour" sx={{ px: 2 }}>
                            Por Hora
                        </ToggleButton>
                        <ToggleButton value="dayOfWeek" sx={{ px: 2 }}>
                            Por Día
                        </ToggleButton>
                        <ToggleButton value="paymentMethod" sx={{ px: 2 }}>
                            Método Pago
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        color="primary"
                        value={metric}
                        exclusive
                        onChange={(e, val) => val && setMetric(val)}
                        size="small"
                    >
                        <ToggleButton value="sales" sx={{ px: 2, color: metric === 'sales' ? activeColor : 'inherit' }}>
                            Ventas ($)
                        </ToggleButton>
                        <ToggleButton value="count" sx={{ px: 2, color: metric === 'count' ? activeColor : 'inherit' }}>
                            Órdenes (#)
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>

            {/* Sub-Filtros (Aparecen condicionalmente) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ height: 40 }}>
                <Box>
                    {view === 'hour' && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filtrar por día</InputLabel>
                            <Select
                                label="Filtrar por día"
                                value={selectedDayOfWeek}
                                onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                            >
                                <MenuItem value="all">Todos los días</MenuItem>
                                <MenuItem value="1">Lunes</MenuItem>
                                <MenuItem value="2">Martes</MenuItem>
                                <MenuItem value="3">Miércoles</MenuItem>
                                <MenuItem value="4">Jueves</MenuItem>
                                <MenuItem value="5">Viernes</MenuItem>
                                <MenuItem value="6">Sábado</MenuItem>
                                <MenuItem value="0">Domingo</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {view !== 'paymentMethod' && (
                    <ToggleButtonGroup
                        value={chartType}
                        exclusive
                        onChange={(e, val) => val && setChartType(val)}
                        size="small"
                    >
                        <ToggleButton value="bar" title="Gráfica de Barras">
                            <BarIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="line" title="Gráfica de Líneas">
                            <LineIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Stack>

            {/* Área de la Gráfica */}
            <Box sx={{ width: '100%', height: 350, mt: 2 }}>
                {chartData.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">
                            No hay datos suficientes para el período seleccionado.
                        </Typography>
                    </Stack>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        {view === 'paymentMethod' ? (
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey={metric}
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={120}
                                    label
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip
                                    formatter={(value: any) =>
                                        metric === 'sales' ? currencyFormatter(value as number) : value
                                    }
                                />
                            </PieChart>
                        ) : chartType === 'bar' ? (
                            <BarChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: '#666666', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fill: '#666666', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(val) => (metric === 'sales' ? `$${val}` : val)}
                                />
                                <RechartsTooltip
                                    cursor={{ fill: '#F4F6F8' }}
                                    contentStyle={{
                                        borderRadius: 8,
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}
                                    formatter={(value: any) => [
                                        metric === 'sales' ? currencyFormatter(value as number) : value,
                                        metric === 'sales' ? 'Venta' : 'Órdenes'
                                    ]}
                                />
                                {/* Barra con borde redondeado y color animado */}
                                <Bar dataKey={metric} fill={activeColor} radius={[6, 6, 0, 0]} maxBarSize={60} />
                            </BarChart>
                        ) : (
                            <LineChart data={chartData} margin={{ top: 20, right: 20, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: '#666666', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fill: '#666666', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(val) => (metric === 'sales' ? `$${val}` : val)}
                                />
                                <RechartsTooltip
                                    contentStyle={{
                                        borderRadius: 8,
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}
                                    formatter={(value: any) => [
                                        metric === 'sales' ? currencyFormatter(value as number) : value,
                                        metric === 'sales' ? 'Venta' : 'Órdenes'
                                    ]}
                                />
                                {/* Línea viva con puntos destacados */}
                                <Line
                                    type="monotone"
                                    dataKey={metric}
                                    stroke={activeColor}
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2, fill: '#FFFFFF' }}
                                    activeDot={{ r: 6, strokeWidth: 0, fill: activeColor }}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                )}
            </Box>
        </Stack>
    );
};

export default DashboardAnalytics;
